import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, getSession } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import DeliveryForm from "@/components/DeliveryForm";
import ProcessedCard from "@/components/ProcessedCard";
import PalletsCard from "@/components/PalletsCard";
import CapturesSection from "@/components/CapturesSection";
import OverflowCard from "@/components/OverflowCard";
import EntriesTable from "@/components/EntriesTable";
import CloseDayDialog from "@/components/CloseDayDialog";
import ReopenDayDialog from "@/components/ReopenDayDialog";
import { Package, Truck, Lock, LockOpen, ArrowRight, Warehouse as WarehouseIcon } from "@phosphor-icons/react";

const closedLabel = (d) =>
  d.closed_by === "auto" ? "Closed automatically" : d.closed_by === "manual" ? "Closed manually" : `Closed by ${d.closed_by}`;

export default function Dashboard() {
  const session = getSession();
  const isManager = session?.role === "manager";
  const isGuest = session?.role === "guest";
  const isAdmin = session?.role === "admin";
  const [config, setConfig] = useState(null);
  const [warehouse, setWarehouse] = useState(isManager ? session.warehouse : "doral");
  const [day, setDay] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    api
      .getConfig()
      .then((c) => {
        setConfig(c);
        setSecondsLeft(c.seconds_until_close);
      })
      .catch(() => toast.error("Failed to load configuration"));
  }, []);

  const refresh = useCallback(async () => {
    if (!config) return;
    try {
      const [d, dels] = await Promise.all([
        api.getDay(config.today, warehouse),
        api.getDeliveries(config.today, warehouse),
      ]);
      setDay(d);
      setDeliveries(dels);
    } catch {
      toast.error("Failed to load today's data");
    } finally {
      setLoading(false);
    }
  }, [config, warehouse]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  // countdown tick
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft === null]);

  // when countdown hits zero the day auto-closes server-side — refresh to reflect it
  useEffect(() => {
    if (secondsLeft === 0 && day?.status === "open") refresh();
  }, [secondsLeft]);

  // poll for the date rolling over so a new day opens at midnight without a manual reload
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const c = await api.getConfig();
        if (c.today !== config?.today) {
          setConfig(c);
          setSecondsLeft(c.seconds_until_close);
        }
      } catch {}
    }, 60000);
    return () => clearInterval(t);
  }, [config?.today]);

  const closed = day?.status === "closed";
  const whConfig = config?.warehouses.find((w) => w.id === warehouse);
  const total = deliveries.reduce((s, d) => s + d.packages, 0);

  const handleError = (e) => {
    if (e?.response?.status === 409) {
      toast.error("Day is closed — refreshing");
      refresh();
    } else {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    }
  };

  const onAdd = async (payload) => {
    try {
      await api.addDelivery({ ...payload, date: config.today, warehouse });
      toast.success("Delivery logged");
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onUpdate = async (id, payload) => {
    try {
      await api.updateDelivery(id, payload);
      toast.success("Entry updated");
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.deleteDelivery(id);
      toast.success("Entry deleted");
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onCloseDay = async () => {
    try {
      await api.closeDay(config.today, warehouse);
      toast.success(`Day closed for ${whConfig.name}. Report saved to archives.`);
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onReopenDay = async () => {
    try {
      await api.reopenDay(config.today, warehouse);
      toast.success(`Day reopened for ${whConfig.name}. It will close automatically at midnight.`);
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onSaveProcessed = async (payload) => {
    try {
      await api.setProcessed(config.today, warehouse, payload);
      toast.success("Processed totals saved");
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onSaveOverflow = async (estimated) => {
    try {
      await api.setOverflow(config.today, warehouse, estimated);
      toast.success("Estimated overflow saved");
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  const onSavePallets = async (payload) => {
    try {
      await api.setPallets(config.today, warehouse, payload);
      toast.success("Total pallets sent saved");
      refresh();
    } catch (e) {
      handleError(e);
    }
  };

  if (!config) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center text-slate-500" data-testid="dashboard-loading">
        Loading...
      </div>
    );
  }

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Today's Entry
          </h1>
          <p className="text-base md:text-lg text-slate-500 mt-1" data-testid="today-subheading">
            Please choose the correct warehouse location before making an entry.
          </p>
        </div>
        <div
          className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
          data-testid="today-date"
        >
          {format(parseISO(config.today), "EEEE, MMM d, yyyy")}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="warehouse-tabs">
        {config.warehouses.filter((w) => !isManager || w.id === session.warehouse).map((w) => (
          <button
            key={w.id}
            data-testid={`warehouse-tab-${w.id}`}
            onClick={() => setWarehouse(w.id)}
            className={`text-left p-5 rounded-md border shadow-sm transition-all duration-200 hover:-translate-y-[1px] ${
              warehouse === w.id
                ? "bg-slate-900 border-slate-900 text-white"
                : "bg-white border-slate-200 text-slate-900 hover:border-slate-300"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <WarehouseIcon size={22} weight="duotone" className={warehouse === w.id ? "text-orange-400" : "text-orange-500"} />
                <span className="font-heading text-xl font-bold tracking-tight">{w.name}</span>
              </div>
              {warehouse === w.id && (
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-orange-400">Active</span>
              )}
            </div>
            <div className={`mt-3 flex flex-wrap gap-1.5 text-xs font-semibold ${warehouse === w.id ? "text-slate-300" : "text-slate-500"}`}>
              {w.carriers.map((c) => (
                <span
                  key={c}
                  className={`px-2 py-0.5 rounded border ${
                    warehouse === w.id ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  {c}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>

      {closed && (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-4"
          data-testid="closed-banner"
        >
          <div className="flex items-center gap-3">
            <Lock size={22} weight="fill" className="text-emerald-600" />
            <div>
              <div className="font-semibold text-emerald-900">This day is closed and locked</div>
              <div className="text-sm text-emerald-700">
                {closedLabel(day)} · the finalized report is in the archives.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <ReopenDayDialog warehouseName={whConfig.name} onConfirm={onReopenDay} />
            )}
            <Link
              to={`/report/${config.today}`}
              data-testid="view-report-link"
              className="inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 rounded-md font-semibold px-4 py-2 text-sm transition-colors duration-200"
            >
              View Report <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Warehouse</div>
          <div className="font-heading text-lg font-bold text-slate-900 mt-1">{whConfig.name}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Package size={14} weight="duotone" /> Packages Today
          </div>
          <div className="font-heading text-3xl font-extrabold text-orange-500 mt-1" data-testid="summary-packages">{total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Truck size={14} weight="duotone" /> Deliveries
          </div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-1" data-testid="summary-deliveries">{deliveries.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Status</div>
          <div className="mt-2" data-testid="day-status">
            {closed ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">
                <Lock size={14} weight="fill" /> Closed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                <LockOpen size={14} weight="fill" /> Open
              </span>
            )}
          </div>
        </div>
      </div>

      {!closed && whConfig && !isGuest && (
        <DeliveryForm warehouse={whConfig} disabled={closed} onAdd={onAdd} />
      )}

      {whConfig && whConfig.id === "doral" && !isGuest && (
        <ProcessedCard day={day} closed={closed} onSave={onSaveProcessed} />
      )}

      {whConfig && !isGuest && (
        <PalletsCard day={day} closed={closed} onSave={onSavePallets} />
      )}

      {whConfig && (
        <CapturesSection date={config.today} warehouse={warehouse} closed={closed || isGuest} />
      )}

      {whConfig && (
        <EntriesTable
          deliveries={deliveries}
          carriers={whConfig.carriers}
          closed={closed || isGuest}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      )}

      {whConfig && (
        <OverflowCard day={day} closed={closed || isGuest} onSave={onSaveOverflow} date={config.today} warehouse={warehouse} />
      )}

      {!closed && whConfig && !isGuest && (
        <div className="flex justify-end">
          <CloseDayDialog
            warehouseName={whConfig.name}
            entryCount={deliveries.length}
            disabled={loading}
            onConfirm={onCloseDay}
          />
        </div>
      )}
    </main>
  );
}
